import { useRef, useEffect } from 'react';
import { Button } from 'components/v4';

function SubmitButton({
    isSubmitting = false,
    colorScheme = 'messenger',
    loadingText = 'Signing in...',
    children,
    ...props
}) {
    const isClickedRef = useRef(false);
    const resetTimerRef = useRef(null);

    // Clear the click guard when a submission finishes (covers the in-flight case)
    useEffect(() => {
        if (!isSubmitting) {
            isClickedRef.current = false;
        }
    }, [isSubmitting]);

    // Clean up any pending reset timer on unmount
    useEffect(
        () => () => {
            if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
        },
        [],
    );

    const handleClick = (e) => {
        // Block only a genuine double-fire: another click already in flight,
        // or a submission is actively in progress.
        if (isClickedRef.current || isSubmitting) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        // Guard against rapid duplicate clicks, then self-clear on the next tick.
        // This prevents the guard from getting permanently stuck when the submit
        // never starts (e.g. Formik validation fails, so isSubmitting never flips).
        isClickedRef.current = true;
        if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
        resetTimerRef.current = setTimeout(() => {
            isClickedRef.current = false;
        }, 0);
    };

    return (
        <Button
            type="submit"
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            disabled={isSubmitting}
            colorScheme={colorScheme}
            loadingText={loadingText}
            pointerEvents={isSubmitting ? 'none' : 'auto'}
            onClick={handleClick}
            _hover={{
                transform: 'translateY(-1px)',
                backgroundColor: 'messenger.600',
                boxShadow: 'lg',
            }}
            _active={{
                transform: 'translateY(0)',
            }}
            {...props}>
            {children}
        </Button>
    );
}

export { SubmitButton };
